import React from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faSlidersH,
  faRectangleHistory,
  faFileCirclePlus,
  faChevronRight,
  faTimes,
} from '@fortawesome/pro-regular-svg-icons';

import {IPolygonData} from 'constants/interfaces';
// eslint-disable-next-line max-len
import {FieldOverview} from 'screens/Dashboard/Customers/CustomerDetail/CustomerDashboardMapping/components/FieldOverview';
// eslint-disable-next-line max-len
import {FieldDataLogDialog} from 'screens/Dashboard/Customers/CustomerDetail/CustomerDashboardMapping/components/FieldDataLogDialog';

import './FieldView.scss';

interface FieldViewProps {
  fields: IPolygonData[];
  farmName: string;
  companyName?: string;
  farmerName?: string;
  farmProvince?: string;
  farmId?: number;

  /** The polygon selected on the map (to auto-open detail). */
  openPolygon?: IPolygonData | null;

  /**
   * The ID of the currently selected field, for highlighting in the list.
   * E.g. if the parent sets selectedFieldId={polygon.id}
   */
  selectedFieldId?: string | number | null;

  /**
   * Callback to notify the parent that a field was selected
   * (either from the list or from the map).
   */
  onSelectField?: (field: IPolygonData) => void;

  /**
   * Callback to refresh fields after update
   */
  onFieldUpdated?: () => void;

  /**
   * Callback when user clicks Edit on a field (opens update form on the right, same as Add new field)
   */
  onEditField?: (field: IPolygonData) => void;

  /**
   * Callback when user clicks Delete on a field. Returns a promise that
   * resolves when the deletion completes successfully.
   */
  onDeleteField?: (field: IPolygonData) => Promise<void> | void;

  /**
   * Callback when user searches for a map location
   */
  onSearchLocation?: (location: {
    lat: number;
    lng: number;
    label?: string;
  }) => void;
}

type LocationSearchMode = 'address' | 'latlong';

interface ILocationPrediction {
  label: string;
  placeId: string;
}

function sanitizeCoordinateInput(value: string) {
  return value.replace(/[^0-9.\-,\s]/g, '');
}

function formatRiskLevel(risk: string | undefined): string {
  if (!risk) return 'No Data';
  const normalized = risk.trim().toLowerCase(); // trim and lowercase
  console.log('Risk check:', risk, '=>', normalized); // see EXACT text

  if (normalized.includes('low')) return 'Low';
  if (normalized.includes('moderate')) return 'Moderate';
  if (normalized.includes('high')) return 'High';
  return 'No Data';
}

function formatPlantedDate(dateStr: string | undefined | null): string {
  if (!dateStr) return 'Not specified';
  try {
    // If the string is purely numeric, parse it as a number first
    const isNumeric = /^\d+$/.test(String(dateStr));
    const parsedDateValue = isNumeric ? Number(dateStr) : dateStr;

    const date = new Date(parsedDateValue);
    if (isNaN(date.getTime())) return 'Not specified';
    // Format as "15 November 2023"
    const options: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    };
    return date.toLocaleDateString('en-US', options);
  } catch {
    return 'Not specified';
  }
}

export const FieldView: React.FC<FieldViewProps> = (props) => {
  const {
    fields,
    farmName,
    companyName,
    farmerName,
    farmProvince,
    farmId,
    openPolygon,
    selectedFieldId,
    onSelectField,
    onEditField,
    onDeleteField,
    onSearchLocation,
  } = props;

  const [checked] = React.useState<string[]>([]);
  const [viewField, setViewField] = React.useState<boolean>(false);
  const [fieldData, setFieldData] = React.useState<IPolygonData | null>(null);
  const [isDataLogOpen, setIsDataLogOpen] = React.useState(false);
  const [dataLogScope, setDataLogScope] = React.useState<'farm' | 'field'>(
    'farm',
  );

  const [fieldSearchTerm, setFieldSearchTerm] = React.useState('');
  const [locationSearchOpen, setLocationSearchOpen] = React.useState(false);
  const [locationSearchMode, setLocationSearchMode] =
    React.useState<LocationSearchMode>('address');
  const [locationDisplayValue, setLocationDisplayValue] = React.useState('');
  const [locationQuery, setLocationQuery] = React.useState('');
  const [locationLatitude, setLocationLatitude] = React.useState('');
  const [locationLongitude, setLocationLongitude] = React.useState('');
  const [locationResults, setLocationResults] = React.useState<
    ILocationPrediction[]
  >([]);
  const [locationLoading, setLocationLoading] = React.useState(false);
  const [locationError, setLocationError] = React.useState('');

  const locationSearchRef = React.useRef<HTMLDivElement | null>(null);
  const autocompleteServiceRef =
    React.useRef<google.maps.places.AutocompleteService | null>(null);
  const geocoderRef = React.useRef<google.maps.Geocoder | null>(null);

  const initializeGoogleLocationServices = React.useCallback(() => {
    if (typeof google === 'undefined' || !google.maps) return false;

    const autocompleteServiceConstructor =
      google.maps.places?.AutocompleteService;
    const geocoderConstructor = google.maps.Geocoder;

    if (
      !autocompleteServiceRef.current &&
      typeof autocompleteServiceConstructor === 'function'
    ) {
      autocompleteServiceRef.current = new autocompleteServiceConstructor();
    }

    if (!geocoderRef.current && typeof geocoderConstructor === 'function') {
      geocoderRef.current = new geocoderConstructor();
    }

    return !!autocompleteServiceRef.current && !!geocoderRef.current;
  }, []);

  const closeLocationSearch = React.useCallback(() => {
    setLocationSearchOpen(false);
    setLocationError('');
    setLocationResults([]);
  }, []);

  // If a new openPolygon arrives from the parent, open its details automatically
  React.useEffect(() => {
    if (openPolygon) {
      // Try to find the latest version from fields array first
      const latestPolygon =
        fields.find((f) => f.id === openPolygon.id) || openPolygon;
      setFieldData(latestPolygon);
      setViewField(true);
    }
  }, [openPolygon, fields]);

  // Sync fieldData with updated fields when fields array changes (e.g., after save)
  // This ensures the displayed field data is always up-to-date
  React.useEffect(() => {
    if (fieldData?.id && fields.length > 0) {
      const updatedField = fields.find((f) => f.id === fieldData.id);
      if (updatedField && updatedField !== fieldData) {
        setFieldData(updatedField);
      }
    }
  }, [fields]);

  // Example toggle logic if you need to track "checked" fields
  const handleToggle = (value: IPolygonData) => (newChecked?: boolean) => {
    // omitted for brevity
  };

  React.useEffect(() => {
    if (initializeGoogleLocationServices()) return;

    const timer = window.setInterval(() => {
      if (initializeGoogleLocationServices()) {
        window.clearInterval(timer);
      }
    }, 300);

    return () => window.clearInterval(timer);
  }, [initializeGoogleLocationServices]);

  React.useEffect(() => {
    if (!locationSearchOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (locationSearchRef.current?.contains(target)) return;
      closeLocationSearch();
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [closeLocationSearch, locationSearchOpen]);

  React.useEffect(() => {
    if (!locationSearchOpen || locationSearchMode !== 'address') return;

    const query = locationQuery.trim();
    if (query.length < 2) {
      setLocationResults([]);
      setLocationLoading(false);
      return;
    }

    if (!autocompleteServiceRef.current) {
      initializeGoogleLocationServices();
    }

    const autocompleteService = autocompleteServiceRef.current;
    if (!autocompleteService) return;

    let cancelled = false;
    const timer = window.setTimeout(() => {
      setLocationLoading(true);
      autocompleteService.getPlacePredictions(
        {
          input: query,
          componentRestrictions: {country: 'za'},
          types: ['geocode'],
        },
        (predictions, status) => {
          if (cancelled) return;

          const nextResults =
            status === google.maps.places.PlacesServiceStatus.OK && predictions
              ? predictions.map((prediction) => ({
                  label: prediction.description,
                  placeId: prediction.place_id,
                }))
              : [];

          setLocationResults(nextResults);
          setLocationLoading(false);
        },
      );
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [locationQuery, locationSearchMode, locationSearchOpen]);

  const moveToLocation = React.useCallback(
    (lat: number, lng: number, label?: string) => {
      const nextLabel = label || `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
      setLocationDisplayValue(nextLabel);
      setLocationQuery(nextLabel);
      setLocationLatitude(String(lat));
      setLocationLongitude(String(lng));
      setLocationSearchOpen(false);
      setLocationError('');
      setLocationResults([]);
      onSearchLocation?.({lat, lng, label: nextLabel});
    },
    [onSearchLocation],
  );

  const selectPrediction = React.useCallback(
    (prediction: ILocationPrediction) => {
      if (!geocoderRef.current) {
        initializeGoogleLocationServices();
      }

      const geocoder = geocoderRef.current;
      if (!geocoder) {
        setLocationError(
          'Google Maps is still loading. Try again in a moment.',
        );
        return;
      }

      setLocationLoading(true);
      geocoder.geocode({placeId: prediction.placeId}, (results, status) => {
        setLocationLoading(false);

        if (
          status === google.maps.GeocoderStatus.OK &&
          results?.[0]?.geometry?.location
        ) {
          const location = results[0].geometry.location;
          moveToLocation(
            location.lat(),
            location.lng(),
            results[0].formatted_address || prediction.label,
          );
          return;
        }

        setLocationError('Could not find that location.');
      });
    },
    [initializeGoogleLocationServices, moveToLocation],
  );

  const handleAddressSubmit = React.useCallback(() => {
    const query = locationQuery.trim();
    if (!query) {
      setLocationError('Enter an address or place to search.');
      return;
    }

    if (locationResults.length > 0) {
      selectPrediction(locationResults[0]);
      return;
    }

    if (!geocoderRef.current) {
      initializeGoogleLocationServices();
    }

    const geocoder = geocoderRef.current;
    if (!geocoder) {
      setLocationError('Google Maps is still loading. Try again in a moment.');
      return;
    }

    setLocationLoading(true);
    geocoder.geocode(
      {
        address: query,
        componentRestrictions: {country: 'ZA'},
      },
      (results, status) => {
        setLocationLoading(false);

        if (
          status === google.maps.GeocoderStatus.OK &&
          results?.[0]?.geometry?.location
        ) {
          const location = results[0].geometry.location;
          moveToLocation(
            location.lat(),
            location.lng(),
            results[0].formatted_address || query,
          );
          return;
        }

        setLocationError('No matching locations were found.');
      },
    );
  }, [
    initializeGoogleLocationServices,
    locationQuery,
    locationResults,
    moveToLocation,
    selectPrediction,
  ]);

  const handleLatLongSubmit = React.useCallback(() => {
    const lat = Number(locationLatitude.trim());
    const lng = Number(locationLongitude.trim());

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      setLocationError('Enter valid latitude and longitude values.');
      return;
    }

    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      setLocationError(
        'Latitude must be between -90 and 90, and longitude between -180 and 180.',
      );
      return;
    }

    moveToLocation(lat, lng);
  }, [locationLatitude, locationLongitude, moveToLocation]);

  // Filter fields by fieldSearchTerm (case-insensitive)
  const filteredFields = fields.filter((field) => {
    const fieldName = field?.info?.fieldName?.toLowerCase() || '';
    const cropName = field?.info?.produce?.name?.toLowerCase() || '';
    const term = fieldSearchTerm.toLowerCase();
    return fieldName.includes(term) || cropName.includes(term);
  });

  const handleOpenFarmDataLog = () => {
    if (!farmId) return;
    setDataLogScope('farm');
    setIsDataLogOpen(true);
  };

  const handleOpenFieldDataLog = () => {
    if (!fieldData?.id) return;
    setDataLogScope('field');
    setIsDataLogOpen(true);
  };

  const renderLocationSearchPanel = () => (
    <div className="location-search-panel">
      <div className="location-search-header">
        <div className="location-search-title">Search Location</div>
        <button
          className="location-search-close"
          onClick={closeLocationSearch}
          type="button"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      <div className="location-mode-row">
        <button
          className={`location-mode-button ${
            locationSearchMode === 'address' ? 'active' : ''
          }`}
          onClick={() => {
            setLocationSearchMode('address');
            setLocationError('');
          }}
          type="button"
        >
          Address
        </button>
        <button
          className={`location-mode-button ${
            locationSearchMode === 'latlong' ? 'active' : ''
          }`}
          onClick={() => {
            setLocationSearchMode('latlong');
            setLocationError('');
          }}
          type="button"
        >
          Lat/Long
        </button>
      </div>

      {locationSearchMode === 'address' ? (
        <>
          <div className="location-helper-text">
            Use the search bar above to find an address, then pick a result to
            move the map.
          </div>

          {locationLoading ? (
            <div className="location-empty-text">Searching locations...</div>
          ) : locationQuery.trim().length < 2 ? (
            <div className="location-empty-text">
              Start typing in the search bar to see matching locations.
            </div>
          ) : locationResults.length > 0 ? (
            <div className="location-results-list">
              {locationResults.map((result) => (
                <button
                  key={result.placeId}
                  className="location-result-row"
                  onClick={() => selectPrediction(result)}
                  type="button"
                >
                  {result.label}
                </button>
              ))}
            </div>
          ) : (
            <div className="location-empty-text">
              No matches yet. Keep typing or press enter to search.
            </div>
          )}
        </>
      ) : (
        <>
          <div className="location-helper-text">
            Enter a latitude and longitude to jump to that point on the map.
          </div>
          <input
            type="text"
            value={locationLatitude}
            placeholder="Latitude (e.g. -26.2041)"
            className="location-coordinate-input"
            onChange={(e) => {
              setLocationLatitude(sanitizeCoordinateInput(e.target.value));
              setLocationError('');
            }}
          />
          <input
            type="text"
            value={locationLongitude}
            placeholder="Longitude (e.g. 28.0473)"
            className="location-coordinate-input"
            onChange={(e) => {
              setLocationLongitude(sanitizeCoordinateInput(e.target.value));
              setLocationError('');
            }}
          />
          <button
            className="location-submit-button"
            onClick={handleLatLongSubmit}
            type="button"
          >
            Go to location
          </button>
        </>
      )}

      {locationError && (
        <div className="location-error-text">{locationError}</div>
      )}
    </div>
  );

  return (
    <div className="fieldview-container">
      <div className="location-search-wrapper" ref={locationSearchRef}>
        <div
          className={`my-custom-search-bar ${
            locationSearchOpen ? 'my-custom-search-bar--active' : ''
          }`}
        >
          <FontAwesomeIcon icon={faSearch} className="search-icon" />
          <input
            type="text"
            placeholder={
              locationSearchMode === 'latlong' && locationSearchOpen
                ? 'Search address or use Lat/Long below'
                : 'Search location'
            }
            className="search-input"
            value={
              locationSearchOpen && locationSearchMode === 'address'
                ? locationQuery
                : locationDisplayValue
            }
            onFocus={() => {
              setLocationSearchMode('address');
              setLocationSearchOpen(true);
              setLocationError('');
            }}
            onChange={(e) => {
              setLocationSearchMode('address');
              setLocationSearchOpen(true);
              setLocationQuery(e.target.value);
              setLocationError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddressSubmit();
              }
            }}
          />
        </div>

        {locationSearchOpen && renderLocationSearchPanel()}
      </div>

      {locationSearchOpen ? null : !viewField ? (
        <div className="fieldview-card">
          {/* Profile row */}
          <div className="profile-row">
            <img
              src="https://storage.googleapis.com/khula-farmer-profiles/clientProfile/1733162829241_.png"
              alt="profile"
              className="profile-image"
            />
            <div className="profile-farmname">{farmName}</div>
            <div className="online-indicator" />
          </div>

          {/* “View data log” + “Generate report” row */}
          <div className="icon-block-row">
            <div
              className="icon-block clickable"
              onClick={handleOpenFarmDataLog}
            >
              <div className="icon-bg">
                <FontAwesomeIcon icon={faRectangleHistory} className="icon" />
              </div>
              <div className="icon-label">View data log</div>
            </div>

            <div className="icon-block clickable">
              <div className="icon-bg">
                <FontAwesomeIcon icon={faFileCirclePlus} className="icon" />
              </div>
              <div className="icon-label">Generate report</div>
            </div>
          </div>

          {/* Field search */}
          <div className="field-search-container">
            <div className="field-search">
              <FontAwesomeIcon icon={faSearch} className="field-search-icon" />
              <input
                type="text"
                placeholder="Search field"
                className="field-search-input"
                value={fieldSearchTerm}
                onChange={(e) => setFieldSearchTerm(e.target.value)}
              />
            </div>
            <button className="field-search-tune-button">
              <FontAwesomeIcon icon={faSlidersH} />
            </button>
          </div>

          {/* Field list */}
          <div className="field-list">
            {filteredFields.map((field, index) => {
              const labelId = `checkbox-list-label-${index}`;
              const disabled = false;
              const fieldName = field?.info?.fieldName || String(index);
              const crop = field?.info?.produce?.name || '';
              const hectares = field?.info?.hectares || 10;
              const isChecked = checked.includes(fieldName);
              const isSelected = field?.id === selectedFieldId;

              return (
                <div
                  key={labelId}
                  className={
                    `field-list-item ` +
                    `${disabled ? 'disabled' : 'clickable'} ` +
                    `${isSelected ? 'field-selected' : ''}`
                  }
                  onClick={() => {
                    if (!disabled) {
                      setViewField(true);
                      setFieldData(field);
                      onSelectField?.(field); // highlight polygon on map
                    }
                  }}
                >
                  <div className="checkbox-wrapper">
                    <input
                      type="checkbox"
                      disabled={disabled}
                      checked={isChecked}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleToggle(field)(e.target.checked);
                      }}
                    />
                    <span className="checkmark" />
                  </div>

                  <div className="field-name">
                    <strong>{field?.info?.fieldName ?? index}</strong> | {crop}{' '}
                    {hectares} Ha
                  </div>

                  {!disabled && (
                    <div className="right-arrow">
                      <FontAwesomeIcon icon={faChevronRight} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="fieldview-card">
          {fieldData && (
            <FieldOverview
              name={fieldData?.info?.fieldName ?? 'No Field Name Found'}
              crop={fieldData?.info?.produce?.name ?? ''}
              areaHa={fieldData?.info?.hectares ?? 10}
              variety={fieldData?.info?.variety || 'Not specified'}
              planted={formatPlantedDate(
                (fieldData?.info as any)?.plantingDate ||
                  (fieldData?.info as any)?.plantedOn,
              )}
              population={fieldData?.info?.plantPopulation?.toString() ?? ''}
              irrigation={fieldData?.info?.irrigation ?? 'N/A'}
              yield={
                (fieldData?.info?.expectedYield?.toString() ?? '') + ' tons/ha'
              }
              onBack={() => {
                setViewField(false);
                setFieldData(null);
              }}
              onEdit={() => {
                onEditField?.(fieldData);
              }}
              onDelete={
                onDeleteField
                  ? async () => {
                      const fieldName =
                        fieldData?.info?.fieldName || 'this field';
                      const confirmDelete = window.confirm(
                        `Are you sure you want to delete "${fieldName}"? This action cannot be undone.`,
                      );
                      if (!confirmDelete) return;
                      try {
                        await onDeleteField(fieldData);
                        setViewField(false);
                        setFieldData(null);
                      } catch (error) {
                        console.error('Failed to delete field:', error);
                      }
                    }
                  : undefined
              }
              onViewDataLog={handleOpenFieldDataLog}
              buyer="Pepsico"
              funder="ABSA"
              // Build the risks array from the last item in fieldData.risks
              // @ts-ignore
              risks={(() => {
                if (!fieldData.risks || fieldData.risks.length === 0) {
                  return [];
                }
                // Take the most recent risk entry
                const latestRisk = fieldData.risks[fieldData.risks.length - 1];
                // @ts-ignore
                return [
                  {
                    label: 'NDVI',
                    // @ts-ignore
                    level: formatRiskLevel(latestRisk.ndviRiskLevel),
                  },
                  {
                    label: 'Moisture',
                    // @ts-ignore
                    level: formatRiskLevel(latestRisk.moistureRiskLevel),
                  },
                  {
                    label: 'Chlorophyll',
                    // @ts-ignore
                    level: formatRiskLevel(latestRisk.clRiskLevel),
                  },
                ];
              })()}
            />
          )}
        </div>
      )}

      <FieldDataLogDialog
        open={isDataLogOpen}
        scope={dataLogScope}
        farmId={farmId}
        fieldId={fieldData?.id}
        fieldName={fieldData?.info?.fieldName}
        farmName={farmName}
        companyName={companyName}
        farmerName={farmerName}
        province={farmProvince}
        onClose={() => setIsDataLogOpen(false)}
      />
    </div>
  );
};
